import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormCreator, FormCreatorProps, FormFieldDef } from "@/components/shared/form-creator";
import { uuid } from "@/interfaces";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet.tsx";

const schema = z.object({
  user_id: z.string().uuid(),
  role: z.string().optional(),
  notes: z.string().optional(),
  is_primary_contact: z.boolean(),
});

type AssignUserForm = z.infer<typeof schema>;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: AssignUserForm) => void;
  users: { id: uuid; name: string; email: string }[];
  initialValues?: Partial<AssignUserForm>;
  disableUserField?: boolean;
};

export function AssignUserModal({ open, onOpenChange, onSubmit, users, initialValues }: Props) {
  const form = useForm<AssignUserForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      user_id: "",
      role: "",
      notes: "",
      is_primary_contact: false,
      ...initialValues,
    },
  });

  useEffect(() => {
    if (initialValues) {
      form.reset({
        ...initialValues,
      });
    }
  }, [initialValues, form]);

  const fields: FormFieldDef[] = [
    {
      type: "select",
      name: "user_id",
      label: "User",
      options: users.map((u) => ({
        label: `${u.name} (${u.email})`,
        value: u.id,
      })),
      required: true,
    },
    { type: "text", name: "role", label: "Role", required: true },
    { type: "textarea", name: "notes", label: "Notes" },
    {
      type: "switch",
      name: "is_primary_contact",
      label: "Primary Contact",
      wrapperClassName: "flex items-center justify-between mt-4",
    },
  ];

  const props: FormCreatorProps = {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    formDefinition: fields,
    buttonText: "Assign User",
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Assign User to Property</SheetTitle>
          <SheetDescription>
            <FormCreator {...props} />
          </SheetDescription>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
}
